import { useEffect, useState } from "react";
import { useSearchParams, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Heart } from "lucide-react";
import { money } from "@/lib/currency";

export default function DonateResult() {
  const location = useLocation();
  const isCancel = location.pathname.includes("cancel");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paypalToken = params.get("token"); // PayPal appends ?token=<order_id>&PayerID=...
  const [status, setStatus] = useState(isCancel ? "cancelled" : "checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (isCancel) return;
    // PayPal return flow — capture the approved order
    if (paypalToken) {
      let attempts = 0;
      const capture = async () => {
        try {
          const { data } = await api.post(`/donations/paypal/capture/${paypalToken}`);
          if (data.payment_status === "paid") { setInfo({ amount: data.amount }); setStatus("success"); return; }
        } catch (e) { /* keep trying */ }
        attempts += 1;
        if (attempts >= 6) { setStatus("timeout"); return; }
        setTimeout(capture, 2000);
      };
      capture();
      return;
    }
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setStatus("success"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch (e) { /* keep trying */ }
      attempts += 1;
      if (attempts >= 8) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, isCancel, paypalToken]);

  const config = {
    checking: { icon: Loader2, spin: true, title: "Confirming your donation…", text: "Please wait a moment.", color: "#475569" },
    success: { icon: CheckCircle2, title: "Thank you for your generosity!", text: "Your gift makes a real difference in our community.", color: "#8C9B86" },
    cancelled: { icon: XCircle, title: "Donation cancelled", text: "No charge was made. You're welcome to try again anytime.", color: "#BE923C" },
    expired: { icon: XCircle, title: "Session expired", text: "Your checkout session expired. Please try again.", color: "#BE923C" },
    timeout: { icon: Loader2, title: "Still processing…", text: "This is taking longer than usual. Check your email for confirmation.", color: "#475569" },
  }[status];

  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-5">
      <div className="grain" />
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6 }}
        data-testid="donate-result"
        className="relative z-10 w-full max-w-md text-center rounded-3xl bg-white border border-[#0F172A]/5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] p-10"
      >
        <span className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${config.color}20`, color: config.color }}>
          <Icon size={30} className={config.spin ? "animate-spin" : ""} />
        </span>
        <h1 className="mt-6 font-serif text-3xl tracking-tight">{config.title}</h1>
        <p className="mt-3 text-[#475569]">{config.text}</p>
        {status === "success" && info?.amount && (
          <p className="mt-4 inline-flex items-center gap-2 text-[#BE923C] font-semibold">
            <Heart size={16} /> {money(info.amount, info.currency)}{info.frequency === "monthly" ? " / month" : ""}
          </p>
        )}
        <Link to="/" data-testid="donate-result-home" className="mt-8 inline-block rounded-full bg-[#0F172A] text-white font-semibold px-8 py-3.5 hover:bg-[#1e293b] transition-colors">
          Return Home
        </Link>
      </motion.div>
    </div>
  );
}
